import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Modal,
  Fade,
  Backdrop,
  Link,
} from '@mui/material';
import { Send, CheckCircle, Close } from '@mui/icons-material';
import { createAppointment } from '../services/api';
import { AppointmentData } from '../types';
import DateTimePicker from './DateTimePicker';

const Appointment: React.FC = () => {
  const [formData, setFormData] = useState<AppointmentData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceId: 1, // Serviço fixo: Avaliação
    date: '',
    time: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<{
    date: string;
    time: string;
    name: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Formatação de telefone (apenas números)
    if (name === 'phone') {
      const numbersOnly = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [name]: numbersOnly,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.firstName || !formData.lastName || 
        !formData.phone || !formData.date || !formData.time) {
      setMessage({
        type: 'error',
        text: 'Por favor, preencha todos os campos obrigatórios.',
      });
      return;
    }

    // Validação de email (apenas se fornecido)
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setMessage({
          type: 'error',
          text: 'Por favor, insira um email válido.',
        });
        return;
      }
    }

    // Validação de data (não pode ser no passado)
    const [year, month, day] = formData.date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    selectedDate.setHours(0, 0, 0, 0);
    
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < todayDate) {
      setMessage({
        type: 'error',
        text: 'A data não pode ser no passado.',
      });
      return;
    }

    // Validação de horário comercial
    const [hours, minutes] = formData.time.split(':').map(Number);
    if (hours < 8 || hours >= 18 || (hours === 17 && minutes > 30)) {
      setMessage({
        type: 'error',
        text: 'Por favor, escolha um horário entre 8h e 18h.',
      });
      return;
    }

    // Validação de intervalo de 30 minutos
    if (minutes !== 0 && minutes !== 30) {
      setMessage({
        type: 'error',
        text: 'Por favor, escolha um horário em intervalos de 30 minutos (ex: 14:00 ou 14:30).',
      });
      return;
    }

    // Validação de telefone (mínimo 10 dígitos)
    if (formData.phone.length < 10) {
      setMessage({
        type: 'error',
        text: 'Por favor, insira um telefone válido com DDD.',
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    const result = await createAppointment(formData);

    setLoading(false);

    if (result.success) {
      // Salvar detalhes do agendamento usando a data sem conversão de timezone
      const [year, month, day] = formData.date.split('-').map(Number);
      const displayDate = new Date(year, month - 1, day);
      
      setAppointmentDetails({
        date: displayDate.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          weekday: 'long',
        }),
        time: formData.time,
        name: `${formData.firstName} ${formData.lastName}`,
      });
      
      // Mostrar modal de sucesso
      setShowSuccessModal(true);
      
      // Limpar formulário
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        serviceId: 1,
        date: '',
        time: '',
      });
    } else {
      setMessage({
        type: 'error',
        text: result.message || 'Erro ao realizar agendamento. Tente novamente.',
      });
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setAppointmentDetails(null);
  };

  return (
    <Box id="appointment" sx={{ py: 10, bgcolor: '#f9f9f9' }}>
      <Container maxWidth="md">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, color: '#9c4f96', mb: 6 }}
        >
          Agende sua Consulta
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: 'white',
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Digite seu nome"
                inputProps={{
                  maxLength: 50,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sobrenome"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Digite seu sobrenome"
                inputProps={{
                  maxLength: 50,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (opcional)"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="seuemail@exemplo.com"
                helperText="Opcional - para receber confirmações"
                inputProps={{
                  maxLength: 100,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                helperText="Digite apenas números"
                inputProps={{
                  maxLength: 11,
                }}
                required
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 5 }} />

          {/* Seletor moderno de data e hora */}
          <DateTimePicker
            selectedDate={formData.date}
            selectedTime={formData.time}
            onDateChange={(date) => setFormData({ ...formData, date })}
            onTimeChange={(time) => setFormData({ ...formData, time })}
          />

          <Divider sx={{ my: 5 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                sx={{
                  bgcolor: '#9c4f96',
                  py: 1.5,
                  '&:hover': { bgcolor: '#8a4785' },
                }}
              >
                {loading ? 'Enviando...' : 'Agendar Consulta'}
              </Button>

              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}
              >
                Ao agendar, você concorda com o uso dos seus dados para contato e confirmação da consulta, 
                conforme nossa{' '}
                <Link 
                  href="/privacidade" 
                  target="_blank"
                  sx={{ 
                    color: '#9c4f96', 
                    textDecoration: 'underline',
                    '&:hover': { color: '#8a4785' }
                  }}
                >
                  Política de Privacidade
                </Link>
                {' '}e{' '}
                <Link 
                  href="/termos" 
                  target="_blank"
                  sx={{ 
                    color: '#9c4f96', 
                    textDecoration: 'underline',
                    '&:hover': { color: '#8a4785' }
                  }}
                >
                  Termos de Uso
                </Link>.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Modal de Sucesso */}
      <Modal
        open={showSuccessModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
        }}
      >
        <Fade in={showSuccessModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              outline: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mb: 2,
              }}
            >
              <Button
                onClick={handleCloseModal}
                sx={{
                  minWidth: 'auto',
                  p: 0.5,
                  color: '#666',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <Close />
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CheckCircle
                sx={{
                  fontSize: 80,
                  color: '#4caf50',
                  mb: 2,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#333',
                  mb: 2,
                }}
              >
                Agendamento Confirmado!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  mb: 3,
                }}
              >
                Seu horário foi reservado com sucesso.
              </Typography>
            </Box>

            {appointmentDetails && (
              <Box
                sx={{
                  bgcolor: '#f9f9f9',
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', textTransform: 'uppercase' }}
                    >
                      Paciente
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#333' }}
                    >
                      {appointmentDetails.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', textTransform: 'uppercase' }}
                    >
                      Data da Consulta
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#333', textTransform: 'capitalize' }}
                    >
                      {appointmentDetails.date}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', textTransform: 'uppercase' }}
                    >
                      Horário
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#333' }}
                    >
                      {appointmentDetails.time}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              Entraremos em contato em breve para confirmar sua consulta.
            </Alert>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleCloseModal}
              sx={{
                bgcolor: '#9c4f96',
                py: 1.5,
                '&:hover': { bgcolor: '#8a4785' },
              }}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Appointment;